import { last } from 'lodash';

import {
  ASSIGNMENT_CORRECTION_KIND,
  SUBMISSION_STATUSES,
} from '@didaskill/domain/assignments/constants';
import { USER_ROLES } from '@didaskill/domain/users/constants';

/**
 * Roles allowed to grant a student another attempt on a corrected assignment.
 */
export const ASSIGNMENT_REDO_ALLOWED_ROLES: readonly USER_ROLES[] = [
  USER_ROLES.ADMIN,
  USER_ROLES.ANIMATION,
  USER_ROLES.SUPER_ADMIN,
];

export type AssignmentRedoIneligibilityReason =
  | 'CORRECTION_NOT_FOUND'
  | 'EVALUATION_REQUIRED'
  | 'NOT_CORRECTED'
  | 'REDO_NOT_ALLOWED';

export type AssignmentSubmissionForRedoCheck = {
  status: string;
  assignment: { multipleDepositsAllowed: boolean };
  deposits: Array<{
    calendlyUrl?: string | null;
    correction?: { correctionKind: ASSIGNMENT_CORRECTION_KIND } | null;
  }>;
};

/**
 * Returns why a submission cannot be reopened for redo, or null when eligible.
 * Mirrors the business checks in allowStudentAssignmentRedo (without persistence).
 *
 * @param submission Submission-like payload (API or mongoose lean shape).
 * @returns Ineligibility reason, or null when redo can be allowed.
 */
export function getAssignmentRedoIneligibilityReason(
  submission: AssignmentSubmissionForRedoCheck,
): AssignmentRedoIneligibilityReason | null {
  if (submission.status !== SUBMISSION_STATUSES.CORRECTED) {
    return 'NOT_CORRECTED';
  }

  const hasOralDeposit = submission.deposits.some((deposit) => deposit.calendlyUrl !== undefined);

  if (hasOralDeposit || !submission.assignment.multipleDepositsAllowed) {
    return 'REDO_NOT_ALLOWED';
  }

  const lastCorrection = last(submission.deposits)?.correction;

  if (!lastCorrection) {
    return 'CORRECTION_NOT_FOUND';
  }

  if (lastCorrection.correctionKind !== ASSIGNMENT_CORRECTION_KIND.EVALUATION) {
    return 'EVALUATION_REQUIRED';
  }

  return null;
}

/**
 * Whether the given roles may trigger the allow-redo action.
 *
 * @param roles Current user roles.
 * @returns True when at least one allowed role is present.
 */
export function canUserAllowAssignmentRedo(roles: USER_ROLES[]): boolean {
  return ASSIGNMENT_REDO_ALLOWED_ROLES.some((role) => roles.includes(role));
}

/**
 * Whether the user can allow a student to redo this submission.
 * Combines role authorization with submission eligibility.
 *
 * @param submission Assignment submission (or compatible shape).
 * @param roles Current user roles.
 * @returns True when the redo action should be allowed.
 */
export function canAllowAssignmentRedo(
  submission: AssignmentSubmissionForRedoCheck,
  roles: USER_ROLES[],
): boolean {
  if (!canUserAllowAssignmentRedo(roles)) {
    return false;
  }

  return getAssignmentRedoIneligibilityReason(submission) === null;
}
